import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ArrowLeft, Search, X, Check, FileDown, Mail, MessageSquare, BookOpen, ChevronDown, ScrollText } from "lucide-react";
import { api, API, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORIES = ["Siding", "Exterior Trim", "Soffit", "Windows & Doors", "Weather Protection & Insulation", "Roofing", "Extra Details"];
const CATEGORY_UNITS = { "Siding": "SQ", "Exterior Trim": "LNFT", "Soffit": "SQFT", "Windows & Doors": "EACH", "Weather Protection & Insulation": "SQFT", "Roofing": "SQ", "Extra Details": "LNFT" };
const FLAGS = [
  { key: "required", label: "Required" },
  { key: "optional", label: "Optional Add-On" },
  { key: "alternate", label: "Alternate Choice" },
  { key: "included", label: "Included (no charge)" },
];
const STATUS_BADGE = {
  draft: "bg-white/15 text-white/70",
  viewed: "bg-sky-400/20 text-sky-300",
  accepted: "bg-emerald-400/20 text-emerald-300",
  edit_requested: "bg-amber-400/20 text-amber-300",
  revision: "bg-violet-400/20 text-violet-300",
};
const STATUS_LABEL = {
  draft: "draft", viewed: "viewed", accepted: "accepted",
  edit_requested: "client wants changes", revision: "open for edits",
};

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;

let rowKey = 0;
let costKey = 0;
const genCostId = () => `cost-${Date.now()}-${++costKey}-${Math.random().toString(36).slice(2, 8)}`;
const COST_METHOD_LABELS = { mile: "per mile", day: "per day", hour: "per hour worked", worker_day: "per worker per day", flat: "flat amount" };
const applyPct = (v, pct) => Math.round((parseFloat(v) || 0) * (1 + (parseFloat(pct) || 0) / 100) * 100) / 100;
const sum2 = (a, b) => Math.round(((parseFloat(a) || 0) + (parseFloat(b) || 0)) * 100) / 100;

const newRow = (lib, riskPct = 0, offerId = "", defaultMode = "full") => {
  const typeLabor = parseFloat(lib.labor_rate ?? 0) || 0;
  const typeMaterial = parseFloat(lib.material_rate ?? 0) || 0;
  const labor = applyPct(typeLabor, riskPct);
  const material = applyPct(typeMaterial, riskPct);
  return {
    key: ++rowKey,
    item_id: lib.id, name: lib.name, category: lib.category, unit: lib.unit,
    base_labor: typeLabor, base_material: typeMaterial,
    item_base_labor: typeLabor, item_base_material: typeMaterial,
    base_rate: sum2(typeLabor, typeMaterial),
    labor_rate: labor, material_rate: material, rate: sum2(labor, material),
    overridden: false, quantity: "", flag: "alternate",
    option_group: lib.category,
    subcategory_id: "", subcategory: "", subcategory_photo: "", subcategories: lib.subcategories || [],
    offered_ids: offerId ? [offerId] : [], offered_notes: {}, offered_prices: {},
    price_mode: defaultMode === "labor" ? "labor" : "full", sub_picked: false,
    special_details: "", customer_description: lib.description || "",
  };
};

const rowFromDoc = (i) => ({
  key: ++rowKey, item_id: i.item_id, name: i.name, category: i.category, unit: i.unit,
  rate: i.rate, base_rate: i.base_rate ?? i.rate, overridden: i.overridden ?? false,
  labor_rate: i.labor_rate ?? 0, material_rate: i.material_rate ?? 0,
  base_labor: i.base_labor ?? i.labor_rate ?? 0, base_material: i.base_material ?? i.material_rate ?? 0,
  subcategory_id: i.subcategory_id || "", subcategory: i.subcategory || "", subcategory_photo: i.subcategory_photo || "", subcategories: [],
  offered_ids: (i.offered_options || []).map((o) => o.id),
  offered_notes: Object.fromEntries((i.offered_options || []).map((o) => [o.id, o.description || ""])),
  offered_prices: Object.fromEntries((i.offered_options || []).filter((o) => o.manual).map((o) => [o.id, { labor: String(o.labor_rate), material: String(o.material_rate) }])),
  price_mode: i.price_mode || "full", sub_picked: true,
  quantity: i.quantity || "", flag: i.flag, option_group: i.option_group,
  special_details: i.special_details || "", customer_description: i.customer_description || "",
});

const rowPayload = (x) => {
  const labor = parseFloat(x.labor_rate) || 0;
  const material = x.price_mode === "labor" ? 0 : (parseFloat(x.material_rate) || 0);
  return {
    item_id: x.item_id, quantity: parseFloat(x.quantity) || 0,
    rate: sum2(labor, material),
    base_rate: x.base_rate ?? sum2(labor, material), overridden: !!x.overridden,
    labor_rate: labor, material_rate: material,
    base_labor: x.base_labor ?? null, base_material: x.base_material ?? null,
    subcategory_id: x.subcategory_id || "", subcategory: x.subcategory || "", subcategory_photo: x.subcategory_photo || "",
    price_mode: x.price_mode || "full",
    offered_options: (x.offered_ids || []).map((id) => {
      const p = x.offered_prices?.[id] || {};
      const hasL = p.labor !== undefined && p.labor !== "";
      const hasM = p.material !== undefined && p.material !== "";
      return {
        id,
        description: x.offered_notes?.[id] ?? "",
        labor_rate: hasL ? parseFloat(p.labor) : null,
        material_rate: hasM ? parseFloat(p.material) : null,
      };
    }),
    flag: x.flag, option_group: x.flag === "alternate" ? (x.option_group || x.category) : "",
    special_details: x.special_details, customer_description: x.customer_description,
  };
};

export const ProposalsTab = () => {
  const [proposals, setProposals] = useState([]);
  const [building, setBuilding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", client_address: "", project_address: "", title: "", notes: "", scope_of_work: "", contract_text: "", attach_contract: true, default_price_mode: "full" });
  const [rows, setRows] = useState([]);
  const [library, setLibrary] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [catOrder, setCatOrder] = useState([]);
  const allCats = [...CATEGORIES, ...customCats.map((c) => c.name)];
  const orderedCats = catOrder.length
    ? [...allCats].sort((a, b) => {
        const ia = catOrder.indexOf(a); const ib = catOrder.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
    : allCats;
  const customUnits = Object.fromEntries(customCats.map((c) => [c.name, c.unit]));
  const unitFor = (c) => CATEGORY_UNITS[c] || customUnits[c] || "EACH";
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const [libSearch, setLibSearch] = useState("");
  const [riskPct, setRiskPct] = useState("0");
  const [categoryModes, setCategoryModes] = useState({});
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ name: "", category: "Siding", labor_rate: "", material_rate: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [est, setEst] = useState({ days: "", workers: "", hours_per_day: "", miles_per_day: "", cost_items: [] });
  const [estOpen, setEstOpen] = useState(false);
  const [showCostAdd, setShowCostAdd] = useState(false);
  const [costDraft, setCostDraft] = useState({ name: "", method: "day", rate: "" });
  const costMergedRef = useRef(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [listTab, setListTab] = useState("pending"); // "pending" | "approved"
  const [tplText, setTplText] = useState("");
  const [tplSaving, setTplSaving] = useState(false);
  const dirtyRef = useRef(false);
  const draftRef = useRef({ form: null, rows: [], editId: null, accepted: false, categoryModes: {} });

  const load = useCallback(() => {
    api.get("/admin/proposals").then(({ data }) => setProposals(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (building) {
      api.get("/admin/library").then(({ data }) => setLibrary(data)).catch(() => {});
      api.get("/admin/library/categories").then(({ data }) => { setCustomCats(data.custom || []); setCatOrder(data.order || []); }).catch(() => {});
    }
  }, [building]);

  useEffect(() => {
    if (!building) return;
    dirtyRef.current = true;
    draftRef.current = { form, rows, editId, accepted, riskPct, categoryModes, est };
  }, [form, rows, editId, building, accepted, riskPct, categoryModes, est]);

  const saveDraft = useCallback(async (keepalive = false) => {
    const snap = draftRef.current;
    if (!snap.form || snap.accepted) return false;
    const { form: f, rows: r, editId: eid } = snap;
    if (!f.client_name.trim() && !f.title.trim() && r.length === 0) return false;
    const payload = {
      ...f, is_draft: true, risk_pct: parseFloat(snap.riskPct) || 0,
      category_modes: snap.categoryModes || {},
      internal_estimate: snap.est || {},
      items: r.map(rowPayload),
    };
    try {
      if (keepalive) {
        const token = localStorage.getItem("is_token");
        await fetch(`${API}${eid ? `/admin/proposals/${eid}` : "/admin/proposals"}`, {
          method: eid ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } else if (eid) {
        await api.patch(`/admin/proposals/${eid}`, payload);
      } else {
        const { data } = await api.post("/admin/proposals", payload);
        setEditId(data.id);
        draftRef.current.editId = data.id;
      }
      dirtyRef.current = false;
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!building) return;
    const handler = () => { if (dirtyRef.current) saveDraft(true); };
    window.addEventListener("beforeunload", handler);
    document.addEventListener("visibilitychange", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      document.removeEventListener("visibilitychange", handler);
      if (dirtyRef.current) saveDraft(true);
    };
  }, [building, saveDraft]);

  const exitBuilder = async () => {
    const saved = await saveDraft();
    if (saved) toast.success("Saved as a draft — reopen it anytime from the list");
    setBuilding(false);
    setEditId(null);
    setAccepted(null);
    load();
  };

  const addRow = (lib, offerId = "") => {
    const existing = rows.find((x) => x.item_id === lib.id);
    if (existing) {
      if (offerId && !(existing.offered_ids || []).includes(offerId)) {
        setRows((r) => r.map((x) => (x.key === existing.key ? { ...x, offered_ids: [...(x.offered_ids || []), offerId] } : x)));
        toast.success(`Option added to the existing ${lib.name} row`);
      } else {
        toast.success(`${lib.name} is already on the proposal — tick options on its row`);
      }
    } else {
      setRows((r) => [...r, newRow(lib, riskPct, offerId, form.default_price_mode)]);
    }
  };

  const toggleOffered = (key, subId) => setRows((r) => r.map((x) => {
    if (x.key !== key) return x;
    const ids = new Set(x.offered_ids || []);
    if (ids.has(subId)) ids.delete(subId); else ids.add(subId);
    return { ...x, offered_ids: [...ids] };
  }));

  const setOfferedNote = (key, subId, val) => setRows((r) => r.map((x) => (x.key === key ? { ...x, offered_notes: { ...(x.offered_notes || {}), [subId]: val } } : x)));

  const setOfferedPrice = (key, subId, field, val) => setRows((r) => r.map((x) => (
    x.key === key ? { ...x, offered_prices: { ...(x.offered_prices || {}), [subId]: { ...(x.offered_prices?.[subId] || {}), [field]: val } } } : x
  )));

  const updateRow = (key, patch) => setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const removeRow = (key) => setRows((r) => r.filter((x) => x.key !== key));
  const duplicateRow = (row) => setRows((r) => [...r, { ...row, key: ++rowKey, flag: "alternate", option_group: row.category }]);

  const applyRisk = (val) => {
    setRiskPct(val);
    setRows((r) => r.map((x) => {
      if (x.overridden) return x;
      const labor = applyPct(x.base_labor, val);
      const material = applyPct(x.base_material, val);
      return { ...x, labor_rate: labor, material_rate: material, rate: sum2(labor, material) };
    }));
  };

  const overrideSplit = (key, field, val) => setRows((r) => r.map((x) => {
    if (x.key !== key) return x;
    const next = { ...x, [field]: val, overridden: true };
    next.rate = sum2(next.labor_rate, next.material_rate);
    return next;
  }));

  const pickSubcategory = (key, subId) => {
    const pct = parseFloat(riskPct) || 0;
    setRows((r) => r.map((x) => {
      if (x.key !== key) return x;
      const lib = library.find((l) => l.id === x.item_id);
      const subs = x.subcategories?.length ? x.subcategories : (lib?.subcategories || []);
      const sub = subs.find((s) => s.id === subId);
      const baseLabor = sub ? sub.labor_rate : (lib?.labor_rate ?? x.item_base_labor ?? x.base_labor ?? 0);
      const baseMaterial = sub ? sub.material_rate : (lib?.material_rate ?? x.item_base_material ?? x.base_material ?? 0);
      const labor = applyPct(baseLabor, pct);
      const material = applyPct(baseMaterial, pct);
      return {
        ...x,
        sub_picked: true,
        subcategory_id: subId, subcategory: sub?.name || "", subcategory_photo: sub?.photo || "", subcategories: subs,
        base_labor: baseLabor, base_material: baseMaterial, base_rate: sum2(baseLabor, baseMaterial),
        labor_rate: labor, material_rate: material, rate: sum2(labor, material),
        overridden: false,
      };
    }));
  };

  const setSharedQty = (category, unit, val) => {
    setRows((r) => r.map((x) => (x.category === category && x.unit === unit ? { ...x, quantity: val } : x)));
  };

  const createCustom = async () => {
    if (!custom.name.trim()) return toast.error("Item name is required");
    try {
      const { data } = await api.post("/admin/library", {
        name: custom.name, category: custom.category, description: custom.description,
        labor_rate: parseFloat(custom.labor_rate) || 0, material_rate: parseFloat(custom.material_rate) || 0,
      });
      toast.success("Custom item saved to library");
      addRow(data);
      setCustom({ name: "", category: "Siding", labor_rate: "", material_rate: "", description: "" });
      setShowCustom(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const emptyEst = { days: "", workers: "", hours_per_day: "", miles_per_day: "", cost_items: [] };
  const estFromDoc = (d) => {
    const e = d || {};
    return {
      days: e.days ? String(e.days) : "",
      workers: e.workers ? String(e.workers) : "",
      hours_per_day: e.hours_per_day ? String(e.hours_per_day) : "",
      miles_per_day: e.miles_per_day ? String(e.miles_per_day) : "",
      cost_items: (e.cost_items || []).map((c) => ({
        id: c.id || genCostId(), library_item_id: c.library_item_id || "",
        name: c.name || "", method: c.method || "flat",
        rate: c.rate || c.rate === 0 ? String(c.rate) : "",
      })),
    };
  };

  // Auto-fill the My Costs rows from the library once it loads (keeps per-proposal rates already set)
  useEffect(() => {
    if (!building || library.length === 0 || costMergedRef.current) return;
    costMergedRef.current = true;
    const libCosts = library.filter((i) => i.category === "My Costs");
    if (libCosts.length === 0) return;
    setEst((prev) => {
      const have = new Set((prev.cost_items || []).map((c) => c.library_item_id).filter(Boolean));
      const added = libCosts.filter((l) => !have.has(l.id)).map((l) => ({
        id: genCostId(), library_item_id: l.id, name: l.name,
        method: l.calc_method || "flat", rate: l.cost_rate ? String(l.cost_rate) : "",
      }));
      return added.length ? { ...prev, cost_items: [...(prev.cost_items || []), ...added] } : prev;
    });
  }, [building, library]);

  const startNew = () => {
    setEditId(null);
    setAccepted(null);
    setRiskPct("0");
    setCategoryModes({});
    setForm({ client_name: "", client_email: "", client_phone: "", client_address: "", project_address: "", title: "", notes: "", scope_of_work: "", contract_text: "", attach_contract: true, default_price_mode: "full" });
    setEst(emptyEst);
    costMergedRef.current = false;
    setRows([]);
    setBuilding(true);
    api.get("/admin/settings/contract").then(({ data }) => setForm((f) => (f.contract_text ? f : { ...f, contract_text: data.template }))).catch(() => {});
  };

  const openProposal = async (id) => {
    try {
      const { data } = await api.get(`/admin/proposals/${id}`);
      if (data.status === "accepted" || data.status === "edit_requested") {
        setAccepted(data);
        setBuilding(true);
        setEditId(id);
        return;
      }
      setEditId(id);
      setForm({ client_name: data.client_name, client_email: data.client_email || "", client_phone: data.client_phone || "", client_address: data.client_address || "", project_address: data.project_address || "", title: data.title, notes: data.notes || "", scope_of_work: data.scope_of_work || "", contract_text: data.contract_text || "", attach_contract: data.attach_contract ?? Boolean(data.contract_text), default_price_mode: data.default_price_mode || "full" });
      setEst(estFromDoc(data.internal_estimate));
      costMergedRef.current = false;
      setRiskPct(String(data.risk_pct || 0));
      setCategoryModes(data.category_modes || {});
      setRows(data.items.map(rowFromDoc));
      setBuilding(true);
    } catch {
      toast.error("Could not open proposal");
    }
  };

  const save = async () => {
    if (!form.client_name.trim() || !form.title.trim()) return toast.error("Client name and project name are required");
    if (rows.some((r) => !r.quantity || parseFloat(r.quantity) <= 0)) return toast.error("Every item needs a quantity");
    const badChoice = rows.find((r) => (r.flag === "optional" || r.flag === "alternate") && !(r.offered_ids || []).length);
    if (badChoice) return toast.error(`"${badChoice.name}" lets the client choose — tick at least one option to offer first`);
    setSaving(true);
    try {
      const payload = {
        ...form,
        risk_pct: parseFloat(riskPct) || 0,
        category_modes: categoryModes,
        internal_estimate: est,
        items: rows.map(rowPayload),
      };
      if (editId) await api.patch(`/admin/proposals/${editId}`, payload);
      else await api.post("/admin/proposals", payload);
      toast.success(editId ? "Proposal updated" : "Proposal created — copy the link and send it");
      dirtyRef.current = false;
      setBuilding(false);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const allowEdit = async (id) => {
    try {
      await api.post(`/admin/proposals/${id}/allow-edit`);
      toast.success("Unlocked — the client can re-open their link and change their selection");
      setBuilding(false);
      setAccepted(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const editAccepted = () => {
    const data = accepted;
    setAccepted(null);
    setForm({ client_name: data.client_name, client_email: data.client_email || "", client_phone: data.client_phone || "", client_address: data.client_address || "", project_address: data.project_address || "", title: data.title, notes: data.notes || "", scope_of_work: data.scope_of_work || "", contract_text: data.contract_text || "", attach_contract: data.attach_contract ?? Boolean(data.contract_text), default_price_mode: data.default_price_mode || "full" });
    setEst(estFromDoc(data.internal_estimate));
    costMergedRef.current = false;
    setRiskPct(String(data.risk_pct || 0));
    setCategoryModes(data.category_modes || {});
    setRows(data.items.map(rowFromDoc));
  };

  const linkFor = (p) => `${window.location.origin}${p.portal_token ? `/portal/${p.portal_token}` : `/proposal/${p.id}`}`;

  const publish = async (p, on = true) => {
    try {
      await api.post(`/admin/proposals/${p.id}/publish`, { published: on });
      toast.success(on ? "Sent — the client can now see it in their portal and link" : "Pulled back — the client can no longer see it");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyLink = (p) => {    navigator.clipboard.writeText(linkFor(p)).then(() => toast.success(p.portal_token ? "Client link copied — they'll see all their proposals in one place" : "Proposal link copied — text or email it to your client"));
  };

  const downloadPdf = async (p) => {
    try {
      const { data } = await api.get(`/admin/proposals/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposal-${p.client_name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not generate the PDF");
    }
  };

  const emailLink = async (p) => {
    try {
      await api.post(`/admin/proposals/${p.id}/remind`, { link: linkFor(p) });
      toast.success(`Proposal emailed to ${p.client_email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const textLink = (p) => {
    const body = `Hi ${p.client_name}, your Infinite Siding proposal is ready: ${linkFor(p)}`;
    window.location.href = `sms:${p.client_phone}?&body=${encodeURIComponent(body)}`;
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete proposal for ${p.client_name}?`)) return;
    try {
      await api.delete(`/admin/proposals/${p.id}`);
      toast.success("Proposal deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const baseTotal = rows.filter((r) => r.flag === "required").reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (r.price_mode === "labor" ? (parseFloat(r.labor_rate) || 0) : sum2(r.labor_rate, r.material_rate)), 0);

  if (building && accepted) {
    const sel = accepted.client_selection;
    const chosen = sel.chosen_lines
      ? [...accepted.items.filter((i) => i.flag === "required"), ...sel.chosen_lines]
      : accepted.items.filter((i) =>
          i.flag === "required" || (i.flag === "optional" && sel.selected_optional.includes(i.id)) || (i.flag === "alternate" && sel.alternates[i.option_group] === i.id)
        );
    return (
      <div data-testid="proposal-accepted-view" className="space-y-5">
        <button onClick={() => { setBuilding(false); setAccepted(null); }} data-testid="accepted-back" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary"><ArrowLeft size={15} aria-hidden="true" /> All Proposals</button>
        <div className={`border rounded-xl p-5 ${accepted.edit_request ? "border-amber-400/40 bg-amber-400/5" : "border-emerald-400/40 bg-emerald-400/5"}`}>
          <p className={`text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${accepted.edit_request ? "text-amber-300" : "text-emerald-300"}`}>
            <Check size={14} aria-hidden="true" /> {accepted.edit_request ? "Client wants changes" : "Approved by client"}
          </p>
          <h2 className="font-display text-3xl mt-2">{accepted.client_name}</h2>
          <p className="text-white/50 text-sm">{accepted.title}</p>
          <p className="font-display text-4xl text-primary mt-4">{fmt(sel.total)}</p>
        </div>
        <div className="space-y-2">
          {chosen.map((i) => (
            <div key={i.id || `${i.item_id}-${i.sub_id}`} className="border border-white/10 rounded-xl bg-card p-3.5 flex justify-between gap-3">
              <div><p className="font-semibold text-sm flex items-center gap-2">{(i.subcategory_photo || i.photo) && <img src={mediaUrl(i.subcategory_photo || i.photo)} alt="" className="w-8 h-8 object-cover border border-white/15" />}<span>{i.name}{i.subcategory ? <span className="text-primary"> — {i.subcategory}</span> : null}</span></p><p className="text-[11px] text-white/45">{i.quantity} {i.unit} × ${i.rate}</p></div>
              <p className="text-primary font-bold text-sm">{fmt(i.total)}</p>
            </div>
          ))}
        </div>
        {sel.client_note && (
          <p className="text-sm text-white/80 bg-white/5 border border-white/10 rounded-xl p-4" data-testid="client-note-admin">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Client note</span>
            {sel.client_note}
          </p>
        )}
        {accepted.edit_request?.note && (
          <p className="text-sm text-amber-200/90 bg-amber-400/10 border border-amber-400/30 rounded-xl p-4" data-testid="edit-request-note-admin">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-1">Client's change request</span>
            {accepted.edit_request.note}
          </p>
        )}
        <div className="flex gap-2 pt-2">
          <button onClick={() => allowEdit(accepted.id)} data-testid="allow-edit-button" className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs py-3.5 transition-transform duration-300 hover:-translate-y-0.5">
            Allow Customer to Edit
          </button>
          <button onClick={editAccepted} data-testid="edit-myself-button" className="flex-1 border border-white/20 text-white/70 font-bold uppercase tracking-widest text-xs py-3.5 hover:border-primary hover:text-primary transition-colors duration-300">
            Edit &amp; Resend Myself
          </button>
        </div>
      </div>
    );
  }

  if (building) {
    return (
      <div data-testid="proposal-builder" className="space-y-5">
        <button onClick={exitBuilder} data-testid="builder-back" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary"><ArrowLeft size={15} aria-hidden="true" /> All Proposals</button>
        <h1 className="font-display text-4xl tracking-wide">{editId ? "EDIT PROPOSAL" : "NEW PROPOSAL"}</h1>
        <div className="border border-white/10 p-4 space-y-3">
          <div className="relative">
            <input value={form.client_name} onChange={(e) => { setForm({ ...form, client_name: e.target.value }); setCustOpen(true); }} onFocus={() => setCustOpen(true)} onBlur={() => setTimeout(() => setCustOpen(false), 150)} placeholder="Client name * — start typing to find saved customers" data-testid="proposal-client-name" className={inputCls} />
            {custOpen && customers.filter((c) => c.name.toLowerCase().includes(form.client_name.trim().toLowerCase()) && c.name.toLowerCase() !== form.client_name.trim().toLowerCase()).length > 0 && (
              <div className="absolute z-20 inset-x-0 top-full mt-1 border border-white/15 bg-[#252720] rounded-lg shadow-xl max-h-56 overflow-y-auto" data-testid="customer-suggestions">
                {customers
                  .filter((c) => c.name.toLowerCase().includes(form.client_name.trim().toLowerCase()) && c.name.toLowerCase() !== form.client_name.trim().toLowerCase())
                  .slice(0, 8)
                  .map((c) => (
                    <button key={c.id} type="button" onMouseDown={() => { setForm({ ...form, client_name: c.name, client_email: c.email || "", client_phone: c.phone || "", client_address: c.address || "" }); setCustOpen(false); }} data-testid={`cust-suggestion-${c.id}`} className="w-full text-left px-4 py-3 hover:bg-primary/10 transition-colors duration-200 border-b border-white/5 last:border-0">
                      <span className="block text-sm font-semibold text-white/90">{c.name}</span>
                      <span className="block text-[11px] text-white/45 truncate">{[c.address, c.phone, c.email].filter(Boolean).join(" · ") || "saved customer"}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
          <input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Billing address — where the client gets billed" data-testid="proposal-client-address" className={inputCls} />
          <input value={form.project_address} onChange={(e) => setForm({ ...form, project_address: e.target.value })} placeholder="Project address — job site (leave blank if same as billing)" data-testid="proposal-project-address" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="Client email" data-testid="proposal-client-email" className={inputCls} />
            <input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="Client phone" data-testid="proposal-client-phone" className={inputCls} />
          </div>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Project name * — e.g. 'Re-side 14 Maple St'" data-testid="proposal-title" className={inputCls} />
          <div data-testid="proposal-price-mode" className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">What are we selling on this proposal?</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button type="button" onClick={() => setForm({ ...form, default_price_mode: "full" })} data-testid="price-mode-full" className={`py-2.5 text-[11px] font-bold uppercase tracking-widest border rounded-lg transition-colors duration-200 ${(form.default_price_mode || "full") === "full" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55"}`}>Labor + Materials</button>
              <button type="button" onClick={() => setForm({ ...form, default_price_mode: "labor" })} data-testid="price-mode-labor" className={`py-2.5 text-[11px] font-bold uppercase tracking-widest border rounded-lg transition-colors duration-200 ${form.default_price_mode === "labor" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55"}`}>Labor Only</button>
            </div>
            <p className="text-[10px] text-white/35">New items you add follow this — you can still switch any category per line below.</p>
          </div>
          <textarea value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} placeholder="Scope of work — visible to the client on the proposal page and PDF" rows={3} data-testid="proposal-scope" className={`${inputCls} resize-none`} />
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes for the client (optional)" rows={2} data-testid="proposal-notes" className={`${inputCls} resize-none`} />
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setContractOpen(!contractOpen)} data-testid="proposal-contract-toggle" className="w-full px-4 py-3 flex items-center justify-between bg-white/5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-primary transition-colors duration-300">
              <span>Contract & signature {form.attach_contract && form.contract_text ? <span className="text-primary">· attached</span> : <span className="text-white/35">· none</span>}</span>
              <ChevronDown size={14} className={`transition-transform duration-300 ${contractOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {contractOpen && (
              <div className="p-3 space-y-2.5">
                <label className="flex items-center gap-2.5 text-sm text-white/80 cursor-pointer" data-testid="proposal-attach-contract">
                  <input type="checkbox" checked={form.attach_contract} onChange={(e) => setForm({ ...form, attach_contract: e.target.checked })} className="w-4 h-4 accent-primary" />
                  Attach contract & signature — client reads and signs when approving
                </label>
                {!form.attach_contract && <p className="text-[11px] text-amber-300/80">No contract goes out — the client just taps approve. Any text below stays saved for later.</p>}
                {form.attach_contract && (<>
                <p className="text-[11px] text-white/40">Goes out with the proposal — the client signs by typing their full name. Placeholders fill in automatically: {"{client_name} {project_title} {address} {total} {date}"}</p>
                <textarea value={form.contract_text} onChange={(e) => setForm({ ...form, contract_text: e.target.value })} rows={9} data-testid="proposal-contract-text" className={`${inputCls} resize-y text-xs leading-relaxed`} />
                <button type="button" onClick={() => api.get("/admin/settings/contract").then(({ data }) => { setForm({ ...form, contract_text: data.template }); toast.success("Reset to standard template"); }).catch(() => {})} data-testid="proposal-contract-reset" className="text-[11px] font-bold uppercase tracking-widest text-white/45 hover:text-primary transition-colors duration-200">
                  Reset to standard template
                </button>
                </>)}
              </div>
            )}
          </div>
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setEstOpen(!estOpen)} data-testid="proposal-estimate-toggle" className="w-full px-4 py-3 flex items-center justify-between bg-white/5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-primary transition-colors duration-300">
              <span>My costs — private {(() => { const d = parseFloat(est.days) || 0; return d ? <span className="text-primary">· {d} day{d === 1 ? "" : "s"} estimated</span> : null; })()}</span>
              <ChevronDown size={14} className={`transition-transform duration-300 ${estOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {estOpen && (() => {
              const dDays = parseFloat(est.days) || 0;
              const dWorkers = parseFloat(est.workers) || 0;
              const dHours = parseFloat(est.hours_per_day) || 0;
              const dMiles = parseFloat(est.miles_per_day) || 0;
              const qtyFor = (m) => m === "mile" ? dMiles * dDays : m === "day" ? dDays : m === "hour" ? dWorkers * dDays * dHours : m === "worker_day" ? dWorkers * dDays : 1;
              const qtyLabel = (m) => m === "mile" ? `${dMiles} mi/day × ${dDays} days` : m === "day" ? `${dDays} days` : m === "hour" ? `${dWorkers} workers × ${dDays} days × ${dHours} h/day` : m === "worker_day" ? `${dWorkers} workers × ${dDays} days` : "one time";
              const lines = (est.cost_items || []).map((c) => {
                const qty = Math.round(qtyFor(c.method) * 100) / 100;
                return { ...c, qty, amount: Math.round(qty * (parseFloat(c.rate) || 0) * 100) / 100 };
              });
              const totalCost = Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100;
              const requiredTotal = Math.round(rows.filter((r) => r.flag === "required").reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0), 0) * 100) / 100;
              const profit = Math.round((requiredTotal - totalCost) * 100) / 100;
              const fld = (key, label, testid) => (
                <div key={key}>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1">{label}</label>
                  <input type="number" min="0" step="0.01" value={est[key]} onChange={(e) => setEst({ ...est, [key]: e.target.value })} data-testid={testid} className={inputCls} />
                </div>
              );
              const setCost = (id, patch) => setEst((p) => ({ ...p, cost_items: p.cost_items.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
              const removeCost = (id) => setEst((p) => ({ ...p, cost_items: p.cost_items.filter((c) => c.id !== id) }));
              const addCustomCost = () => {
                if (!costDraft.name.trim()) return toast.error("Name the cost first");
                setEst((p) => ({ ...p, cost_items: [...(p.cost_items || []), { id: genCostId(), library_item_id: "", name: costDraft.name.trim(), method: costDraft.method, rate: costDraft.rate }] }));
                setCostDraft({ name: "", method: "day", rate: "" });
                setShowCostAdd(false);
              };
              return (
                <div className="p-3 space-y-3" data-testid="proposal-estimate-panel">
                  <p className="text-[11px] text-white/40">Only you see this — never the client or the crew. Rates come from Library → My Costs; any price you change here is for this proposal only. The project page compares this estimate against real payroll and expenses.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {fld("days", "Days on site", "est-days")}
                    {fld("workers", "Workers", "est-workers")}
                    {fld("hours_per_day", "Hours per day", "est-hours-day")}
                    {fld("miles_per_day", "Miles per day", "est-miles-day")}
                  </div>
                  {lines.length === 0 && <p className="text-[11px] text-white/40">No cost items yet — set up your rates in Library → My Costs, or add a custom one below.</p>}
                  <div className="space-y-2">
                    {lines.map((l) => (
                      <div key={l.id} className="border border-white/10 rounded-lg bg-background/60 p-2.5 space-y-1.5" data-testid={`est-cost-${l.id}`}>
                        <div className="flex items-center gap-2">
                          {l.library_item_id ? (
                            <span className="flex-1 min-w-0 text-xs font-semibold text-white/85 truncate">{l.name}</span>
                          ) : (
                            <input value={l.name} onChange={(e) => setCost(l.id, { name: e.target.value })} placeholder="Cost name" aria-label="Cost name" data-testid={`est-cost-name-${l.id}`} className={inputCls} />
                          )}
                          <button onClick={() => removeCost(l.id)} aria-label={`Remove ${l.name || "cost"}`} data-testid={`est-cost-remove-${l.id}`} className="text-red-400/70 hover:text-red-300 p-1 shrink-0"><X size={13} /></button>
                        </div>
                        <div className="flex items-center gap-2">
                          {l.library_item_id ? (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 shrink-0">{COST_METHOD_LABELS[l.method] || l.method}</span>
                          ) : (
                            <select value={l.method} onChange={(e) => setCost(l.id, { method: e.target.value })} aria-label="How is it calculated" data-testid={`est-cost-method-${l.id}`} className={`${selectCls} flex-1`}>
                              {Object.entries(COST_METHOD_LABELS).map(([k, lbl]) => <option key={k} value={k} className="bg-[#252720]">{lbl}</option>)}
                            </select>
                          )}
                          <input type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setCost(l.id, { rate: e.target.value })} aria-label="Rate" data-testid={`est-cost-rate-${l.id}`} className={`${inputCls} !w-24 shrink-0`} placeholder="$ rate" />
                          <span className="ml-auto text-xs font-bold text-primary shrink-0" data-testid={`est-cost-amount-${l.id}`}>${l.amount.toFixed(2)}</span>
                        </div>
                        <p className="text-[10px] text-white/35">{qtyLabel(l.method)} → {l.qty} × ${parseFloat(l.rate) || 0}</p>
                      </div>
                    ))}
                  </div>
                  {showCostAdd ? (
                    <div className="border border-dashed border-primary/40 rounded-lg p-2.5 space-y-2" data-testid="est-cost-custom-form">
                      <input value={costDraft.name} onChange={(e) => setCostDraft({ ...costDraft, name: e.target.value })} placeholder="Cost name — e.g. 'Dumpster'" data-testid="est-cost-custom-name" className={inputCls} />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={costDraft.method} onChange={(e) => setCostDraft({ ...costDraft, method: e.target.value })} aria-label="How is it calculated" data-testid="est-cost-custom-method" className={selectCls}>
                          {Object.entries(COST_METHOD_LABELS).map(([k, lbl]) => <option key={k} value={k} className="bg-[#252720]">{lbl}</option>)}
                        </select>
                        <input value={costDraft.rate} onChange={(e) => setCostDraft({ ...costDraft, rate: e.target.value })} type="number" min="0" step="0.01" placeholder="$ rate" data-testid="est-cost-custom-rate" className={inputCls} />
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={addCustomCost} data-testid="est-cost-custom-add" className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[11px] py-2.5 rounded-lg">Add cost</button>
                        <button type="button" onClick={() => setShowCostAdd(false)} data-testid="est-cost-custom-cancel" className="px-4 border border-white/20 text-white/60 text-[11px] font-bold uppercase tracking-widest rounded-lg">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowCostAdd(true)} data-testid="est-cost-custom-toggle" className="flex items-center gap-2 border border-dashed border-white/25 rounded-lg px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 w-full justify-center">
                      <Plus size={13} aria-hidden="true" /> Add another cost
                    </button>
                  )}
                  {totalCost > 0 && (
                    <div className="border border-white/10 rounded-lg bg-background/60 p-3 space-y-1 text-[11px]" data-testid="est-preview">
                      <p className="flex justify-between font-bold text-primary"><span>Estimated total cost</span><span data-testid="est-total-cost">${totalCost.toFixed(2)}</span></p>
                      {requiredTotal > 0 && (
                        <p className="flex justify-between font-bold border-t border-white/10 pt-1.5 mt-1"><span className="text-white/70">Expected profit (required items {fmt(requiredTotal)})</span><span className={profit >= 0 ? "text-white/90" : "text-red-400"} data-testid="est-expected-profit">${profit.toFixed(2)}</span></p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={() => { setPickerOpen(!pickerOpen); setShowCustom(false); }} data-testid="add-library-item-toggle" className={`flex-1 flex items-center justify-center gap-2 border text-xs font-bold uppercase tracking-widest py-3.5 transition-colors duration-300 ${pickerOpen ? "bg-primary text-primary-foreground border-primary" : "border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"}`}>
            <BookOpen size={14} aria-hidden="true" /> Add Library Item
          </button>
          <button onClick={() => { setShowCustom(!showCustom); setPickerOpen(false); }} data-testid="custom-item-toggle" className={`flex-1 flex items-center justify-center gap-2 border text-xs font-bold uppercase tracking-widest py-3.5 transition-colors duration-300 ${showCustom ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/70 hover:border-primary hover:text-primary"}`}>
            <Plus size={14} aria-hidden="true" /> Add Custom Item
          </button>
        </div>

        {pickerOpen && (
          <div className="border border-white/10 bg-card" data-testid="library-picker">
            <div className="flex items-center gap-2 border-b border-white/10 px-4">
              <Search size={15} className="text-white/40 shrink-0" aria-hidden="true" />
              <input value={libSearch} onChange={(e) => setLibSearch(e.target.value)} placeholder="Search items — e.g. 'PVC garage'" data-testid="picker-search" className="w-full bg-transparent py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none" />
            </div>
            {library.length === 0 && <p className="text-white/40 text-sm py-6 text-center">Loading library...</p>}
            {orderedCats.map((cat) => {
              const q = libSearch.toLowerCase();
              const catItems = library.filter((i) => i.category === cat).filter((i) =>
                !q || i.name.toLowerCase().includes(q) || (i.subcategories || []).some((s) => s.name.toLowerCase().includes(q))
              );
              if (q && catItems.length === 0) return null;
              const open = expandedCat === cat || !!libSearch;
              return (
                <div key={cat} className="border-b border-white/10 last:border-0">
                  <button onClick={() => { setExpandedCat(open && !libSearch ? null : cat); setExpandedType(null); }} data-testid={`picker-cat-${cat}`} className={`w-full px-4 py-4 flex items-center justify-between transition-colors duration-200 ${open ? "bg-primary/10" : "bg-white/[0.06] hover:bg-white/10"}`}>
                    <span className="font-display text-xl tracking-wider text-primary uppercase">{cat}</span>
                    <span className="flex items-center gap-2 text-white/40 text-[11px] font-semibold">{catItems.length} types <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180 text-primary" : ""}`} aria-hidden="true" /></span>
                  </button>
                  {open && catItems.map((item) => {
                    const subs = item.subcategories || [];
                    const typeMatch = q && item.name.toLowerCase().includes(q);
                    const visSubs = q && !typeMatch ? subs.filter((s) => s.name.toLowerCase().includes(q)) : subs;
                    const typeOpen = expandedType === item.id || !!q;
                    return (
                      <div key={item.id} data-testid={`picker-item-${item.id}`}>
                        <div className={`flex items-stretch border-t border-white/5 ${typeOpen && subs.length > 0 ? "bg-white/[0.06]" : ""}`}>
                          <button onClick={() => addRow(item)} data-testid={`picker-type-${item.id}`} className="flex-1 pl-7 pr-2 py-3.5 flex items-center gap-2.5 text-left hover:bg-primary/10 transition-colors duration-200 min-w-0">
                            <Plus size={14} className="text-primary shrink-0" aria-hidden="true" />
                            {item.photos?.[0] && <img src={mediaUrl(item.photos[0])} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" loading="lazy" />}
                            <span className="text-sm text-white/85 truncate">{item.name}</span>
                          </button>
                          {subs.length > 0 && (
                            <button onClick={() => setExpandedType(typeOpen && !libSearch ? null : item.id)} data-testid={`picker-expand-${item.id}`} aria-label={`Browse ${item.name} options`} className="px-3.5 flex items-center gap-1.5 text-[11px] font-semibold text-white/40 hover:text-primary transition-colors duration-200 shrink-0">
                              {subs.length} option{subs.length === 1 ? "" : "s"} <ChevronDown size={13} className={`transition-transform duration-300 ${typeOpen ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                        {typeOpen && visSubs.length > 0 && (
                          <p className="pl-12 pr-4 pt-2.5 text-[10px] text-white/35 uppercase tracking-widest font-bold">Tap an option to add {item.name} with it offered — or tap the type name above to add it first</p>
                        )}
                        {typeOpen && visSubs.map((s) => (
                          <button key={s.id} onClick={() => addRow(item, s.id)} data-testid={`picker-sub-${s.id}`} className="w-full pl-12 pr-4 py-3 flex items-center justify-between text-left border-t border-white/5 bg-white/[0.02] hover:bg-primary/10 transition-colors duration-200 group">
                            <span className="flex items-center gap-2.5 min-w-0">
                              {s.photo
                                ? <img src={mediaUrl(s.photo)} alt={s.name} className="w-9 h-9 object-cover border border-white/10 shrink-0" loading="lazy" />
                                : <Plus size={14} className="text-primary shrink-0" aria-hidden="true" />}
                              <span className="min-w-0">
                                <span className="block text-sm text-white/85 truncate">{s.name}</span>
                                {s.description && <span className="block text-[11px] text-white/40 truncate">{s.description}</span>}
                              </span>
                            </span>
                            <span className="text-[11px] text-primary font-semibold shrink-0 ml-3">${sum2(s.labor_rate, s.material_rate).toFixed(2)}/{item.unit}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {showCustom && (
          <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="custom-item-form">
            <p className="text-[11px] text-white/50">Created once — saved to your library automatically, reusable on every future proposal.</p>
            <input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Item name — e.g. 'PVC Garage Door Surround'" data-testid="custom-item-name" className={inputCls} />
            <div className="grid grid-cols-1 gap-3">
              <select value={custom.category} onChange={(e) => setCustom({ ...custom, category: e.target.value })} data-testid="custom-item-category" className={selectCls}>
                {allCats.map((c) => <option key={c} value={c} className="bg-[#252720]">{c}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input value={custom.labor_rate} onChange={(e) => setCustom({ ...custom, labor_rate: e.target.value })} type="number" min="0" step="0.01" placeholder={`Labor $/${unitFor(custom.category)}`} data-testid="custom-item-labor" className={inputCls} />
                <input value={custom.material_rate} onChange={(e) => setCustom({ ...custom, material_rate: e.target.value })} type="number" min="0" step="0.01" placeholder={`Material $/${unitFor(custom.category)}`} data-testid="custom-item-material" className={inputCls} />
              </div>
              <p className="text-[11px] text-white/45">Customer sees the combined rate: <span className="text-primary font-bold">${((parseFloat(custom.labor_rate) || 0) + (parseFloat(custom.material_rate) || 0)).toFixed(2)}/{unitFor(custom.category)}</span> — the split is admin-only.</p>
            </div>
            <input value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} placeholder="Description for clients (optional)" data-testid="custom-item-description" className={inputCls} />
            <button onClick={createCustom} data-testid="custom-item-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs py-3">Save & Add to Proposal</button>
          </div>
        )}

        <div className="border border-white/10 bg-card p-4" data-testid="risk-card">
          <p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-2">Project Risk / Difficulty Adjustment</p>
          <div className="flex items-center gap-3">
            <input value={riskPct} onChange={(e) => applyRisk(e.target.value)} type="number" min="0" max="100" step="0.5" data-testid="risk-input" className={`${inputCls} !w-28`} />
            <p className="text-white/45 text-xs leading-relaxed">% added to standard rates — <span className="text-primary font-bold">this project only</span>. Library prices never change, and the client never sees this.</p>
          </div>
        </div>

        <div className="space-y-9" data-testid="builder-items">
          {[...new Set(rows.map((r) => r.category))].map((cat) => {
            const catRows = rows.filter((r) => r.category === cat);
            const units = [...new Set(catRows.map((r) => r.unit))];
            return (
              <div key={cat} data-testid={`builder-cat-${cat}`} className="rounded-2xl border border-white/15 bg-white/[0.03] p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 border-b border-white/10 pb-3">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">{cat}</p>
                  {units.map((u) => {
                    const matching = catRows.filter((r) => r.unit === u);
                    const qtys = [...new Set(matching.map((r) => String(r.quantity)))];
                    const shared = qtys.length === 1 ? qtys[0] : "";
                    return (
                      <label key={u} className="flex items-center gap-2 text-[11px] text-white/50">
                        Quantity for all:
                        <input value={shared} onChange={(e) => setSharedQty(cat, u, e.target.value)} type="number" min="0" step="0.01" placeholder="—" data-testid={`shared-qty-${cat}`} className="w-24 bg-white/5 border border-white/15 px-2.5 py-2 text-sm text-white focus:outline-none focus:border-primary" />
                        <span className="text-primary font-bold">{u}</span>
                      </label>
                    );
                  })}
                  {catRows.some((r) => r.flag === "optional" || r.flag === "alternate") && (
                    <div className="flex items-center gap-1.5" data-testid={`cat-mode-${cat}`}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 mr-1">Client picks:</span>
                      <button onClick={() => setCategoryModes((m) => ({ ...m, [cat]: "single" }))} data-testid={`cat-mode-single-${cat}`} className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${(categoryModes[cat] || "single") === "single" ? "border-primary text-primary bg-primary/10" : "border-white/15 text-white/45 hover:border-white/30"}`}>One type</button>
                      <button onClick={() => setCategoryModes((m) => ({ ...m, [cat]: "multiple" }))} data-testid={`cat-mode-multiple-${cat}`} className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${categoryModes[cat] === "multiple" ? "border-primary text-primary bg-primary/10" : "border-white/15 text-white/45 hover:border-white/30"}`}>Multiple types</button>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {catRows.map((row) => {
                    const libRow = library.find((l) => l.id === row.item_id);
                    const rowSubs = row.subcategories?.length ? row.subcategories : (libRow?.subcategories || []);
                    const isChoice = row.flag === "optional" || row.flag === "alternate";
                    const qty = parseFloat(row.quantity) || 0;
                    const effOpt = (s) => {
                      const ov = row.offered_prices?.[s.id] || {};
                      const hasOv = (ov.labor ?? "") !== "" || (ov.material ?? "") !== "";
                      const l = hasOv ? (ov.labor ? parseFloat(ov.labor) || 0 : (parseFloat(s.labor_rate) || 0)) : applyPct(s.labor_rate, riskPct);
                      const m = hasOv ? (ov.material ? parseFloat(ov.material) || 0 : (parseFloat(s.material_rate) || 0)) : applyPct(s.material_rate, riskPct);
                      return { l, m, hasOv, rate: row.price_mode === "labor" ? l : sum2(l, m) };
                    };
                    const offeredRates = rowSubs.filter((s) => (row.offered_ids || []).includes(s.id)).map((s) => effOpt(s).rate);
                    const lockedRate = row.price_mode === "labor" ? (parseFloat(row.labor_rate) || 0) : sum2(row.labor_rate, row.material_rate);
                    const rangeText = offeredRates.length === 0 ? "—" : offeredRates.length === 1 ? fmt(qty * offeredRates[0]) : `${fmt(qty * Math.min(...offeredRates))} – ${fmt(qty * Math.max(...offeredRates))}`;
                    return (
                    <div key={row.key} className="border border-white/20 rounded-xl bg-card shadow-[0_4px_20px_rgba(0,0,0,0.3)] p-4 space-y-3" data-testid={`builder-row-${row.key}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-sm flex items-center gap-2">
                            {row.subcategory_photo && <img src={mediaUrl(row.subcategory_photo)} alt="" className="w-8 h-8 object-cover border border-white/15" data-testid={`row-sub-photo-${row.key}`} />}
                            <span>{row.name}{row.subcategory ? <span className="text-primary"> — {row.subcategory}</span> : null}</span>
                          </p>
                          <p className="text-[11px] text-white/45">priced per {row.unit} · base labor ${row.base_labor} + material ${row.base_material}</p>
                        </div>
                        <button onClick={() => removeRow(row.key)} aria-label="Remove item" data-testid={`row-remove-${row.key}`} className="text-red-400/70 hover:text-red-300 p-1"><X size={16} /></button>
                      </div>
                      <select value={row.flag} onChange={(e) => updateRow(row.key, { flag: e.target.value, option_group: row.category })} data-testid={`row-flag-${row.key}`} className={selectCls}>
                        {FLAGS.map((f) => <option key={f.key} value={f.key} className="bg-[#252720]">{f.label}</option>)}
                      </select>
                      <input value={row.customer_description} onChange={(e) => updateRow(row.key, { customer_description: e.target.value })} placeholder="Customer description (optional)" data-testid={`row-desc-${row.key}`} className={inputCls} />
                      {!isChoice && rowSubs.length > 0 && (
                        <select value={row.subcategory_id || ""} onChange={(e) => pickSubcategory(row.key, e.target.value)} data-testid={`row-subcategory-${row.key}`} className={selectCls}>
                          <option value="" disabled={!row.sub_picked && !row.subcategory_id} className="bg-[#252720]">{row.sub_picked || row.subcategory_id ? `Standard — base price ($${sum2(row.item_base_labor ?? libRow?.labor_rate ?? row.base_labor, row.item_base_material ?? libRow?.material_rate ?? row.base_material)}/${row.unit})` : "Choose the option — e.g. Hardie Plank..."}</option>
                          {rowSubs.map((s) => <option key={s.id} value={s.id} className="bg-[#252720]">{s.name} — ${sum2(s.labor_rate, s.material_rate).toFixed(2)}/{row.unit}</option>)}
                        </select>
                      )}
                      {isChoice && rowSubs.length === 0 && (
                        <p className="text-[11px] text-amber-300/80 border border-amber-400/30 bg-amber-400/5 px-3 py-2.5">This type has no options in the library yet — add options in the Library tab so the client has something to pick.</p>
                      )}
                      {isChoice && rowSubs.length > 0 && (
                        <div className="border border-white/10 p-3 space-y-1.5" data-testid={`row-offered-${row.key}`}>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Options offered to the client — tick what they can choose from</p>
                          {rowSubs.map((s) => {
                            const on = (row.offered_ids || []).includes(s.id);
                            const eff = effOpt(s);
                            const oLabor = eff.l;
                            const oMaterial = eff.m;
                            const oRate = eff.rate;
                            const ov = row.offered_prices?.[s.id] || {};
                            return (
                              <div key={s.id} className={`border transition-colors duration-200 ${on ? "border-primary/60 bg-primary/10" : "border-white/10"}`}>
                                <button onClick={() => toggleOffered(row.key, s.id)} data-testid={`row-offer-${row.key}-${s.id}`} className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left hover:bg-white/[0.03] transition-colors duration-200">
                                  <span className={`w-4 h-4 border-2 flex items-center justify-center shrink-0 transition-colors duration-200 ${on ? "bg-primary border-primary" : "border-white/30"}`}>
                                    {on && <Check size={11} className="text-primary-foreground" aria-hidden="true" />}
                                  </span>
                                  {s.photo && <img src={mediaUrl(s.photo)} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" loading="lazy" />}
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-xs text-white/85 truncate">{s.name}</span>
                                    <span className="block text-[10px] text-white/40">labor ${oLabor.toFixed(2)} + material ${oMaterial.toFixed(2)}</span>
                                  </span>
                                  <span className="text-[11px] text-primary font-semibold shrink-0">${oRate.toFixed(2)}/{row.unit}</span>
                                </button>
                                {on && (
                                  <div className="px-2.5 pb-2 space-y-1.5">
                                    <input
                                      value={row.offered_notes?.[s.id] ?? s.description ?? ""}
                                      onChange={(e) => setOfferedNote(row.key, s.id, e.target.value)}
                                      placeholder="Description for this option on this proposal (optional)"
                                      data-testid={`row-offer-desc-${row.key}-${s.id}`}
                                      className="w-full bg-background/60 border border-white/10 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-primary transition-colors duration-300"
                                    />
                                    <div className="grid grid-cols-2 gap-1.5">
                                      <input
                                        value={ov.labor ?? ""}
                                        onChange={(e) => setOfferedPrice(row.key, s.id, "labor", e.target.value)}
                                        type="number" min="0" step="0.01"
                                        placeholder={`labor $${applyPct(s.labor_rate, riskPct).toFixed(2)} (library)`}
                                        data-testid={`row-offer-labor-${row.key}-${s.id}`}
                                        className="bg-background/60 border border-white/10 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-primary transition-colors duration-300"
                                      />
                                      <input
                                        value={ov.material ?? ""}
                                        onChange={(e) => setOfferedPrice(row.key, s.id, "material", e.target.value)}
                                        type="number" min="0" step="0.01"
                                        placeholder={`material $${applyPct(s.material_rate, riskPct).toFixed(2)} (library)`}
                                        data-testid={`row-offer-material-${row.key}-${s.id}`}
                                        className="bg-background/60 border border-white/10 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-primary transition-colors duration-300"
                                      />
                                    </div>
                                    <p className="text-[9px] text-white/30">Blank boxes use the library price{riskPct !== "0" ? " + risk %" : ""}. Filled boxes = manual price for this proposal only (risk % skipped).</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {(row.offered_ids || []).length === 0 && <p className="text-[10px] text-amber-300/80">No options ticked — the client won't be able to pick this type yet.</p>}
                        </div>
                      )}
                      {(isChoice ? (row.offered_ids || []).length > 0 : (row.sub_picked || rowSubs.length === 0)) && (
                        <div className="border border-white/10 rounded-xl bg-background/50 p-3.5 space-y-3" data-testid={`row-pricing-${row.key}`}>
                          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`row-pricemode-${row.key}`}>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 mr-1">Charge:</span>
                            <button onClick={() => updateRow(row.key, { price_mode: "full" })} data-testid={`row-pricemode-full-${row.key}`} className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${(row.price_mode || "full") === "full" ? "border-primary text-primary bg-primary/10" : "border-white/15 text-white/45 hover:border-white/30"}`}>Labor + Material</button>
                            <button onClick={() => updateRow(row.key, { price_mode: "labor" })} data-testid={`row-pricemode-labor-${row.key}`} className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${row.price_mode === "labor" ? "border-primary text-primary bg-primary/10" : "border-white/15 text-white/45 hover:border-white/30"}`}>Labor only</button>
                            {row.price_mode === "labor" && <span className="text-[10px] text-amber-300/90">— client is charged labor × qty only</span>}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 block mb-1">Qty ({row.unit})</label>
                              <input value={row.quantity} onChange={(e) => updateRow(row.key, { quantity: e.target.value })} type="number" min="0" step="0.01" data-testid={`row-qty-${row.key}`} className={inputCls} />
                            </div>
                            {isChoice ? (
                              <div>
                                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 block mb-1">Total</label>
                                <p className="py-3.5 px-4 text-sm text-white/60" data-testid={`row-total-${row.key}`}>{rangeText}<span className="block text-[10px] text-white/35">decided by the client's pick</span></p>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 block mb-1">Labor $/{row.unit}</label>
                                  <input value={row.labor_rate} onChange={(e) => overrideSplit(row.key, "labor_rate", e.target.value)} type="number" min="0" step="0.01" data-testid={`row-labor-${row.key}`} className={inputCls} />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 block mb-1">Material $/{row.unit}</label>
                                  <input value={row.material_rate} onChange={(e) => overrideSplit(row.key, "material_rate", e.target.value)} type="number" min="0" step="0.01" data-testid={`row-material-${row.key}`} className={inputCls} />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 block mb-1">Total</label>
                                  <p className="py-3.5 px-4 font-display text-xl text-primary" data-testid={`row-total-${row.key}`}>{fmt(qty * lockedRate)}</p>
                                </div>
                              </>
                            )}
                          </div>
                          {isChoice ? (
                            <p className="text-[10px] text-white/35" data-testid={`row-client-rate-${row.key}`}>Client sees each ticked option's combined price and picks inside <span className="text-primary font-bold">{row.category}</span> — the labor/material split stays hidden.</p>
                          ) : (
                            <p className="text-[10px] text-white/35" data-testid={`row-client-rate-${row.key}`}>
                              Client rate: <span className="text-primary font-bold">${lockedRate.toFixed(2)}/{row.unit}</span> ({row.price_mode === "labor" ? "labor only" : "labor + material combined"} — split visible only to you)
                              {row.overridden && <span className="text-amber-300"> · manual price</span>}
                            </p>
                          )}
                          <textarea value={row.special_details} onChange={(e) => updateRow(row.key, { special_details: e.target.value })} placeholder="Special Details / Specifications — style, manufacturer, color, layout..." rows={2} data-testid={`row-specs-${row.key}`} className={`${inputCls} resize-none`} />
                        </div>
                      )}
                      <button onClick={() => duplicateRow(row)} data-testid={`row-duplicate-${row.key}`} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                        <Copy size={13} aria-hidden="true" /> Duplicate
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && <p className="text-white/40 text-sm py-8 text-center border border-dashed border-white/15">No items yet — add from the library above.</p>}
        </div>

        <div className="border border-white/10 p-4 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-white/50">Required items total</span>
          <span className="font-display text-3xl text-primary" data-testid="builder-base-total">{fmt(baseTotal)}</span>
        </div>
        <button onClick={save} disabled={saving} data-testid="proposal-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50">
          {saving ? "Saving..." : editId ? "Update Proposal" : "Create Proposal"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-proposals-tab" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-4xl tracking-wide">PROPOSALS</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => { setTplOpen(!tplOpen); if (!tplText) api.get("/admin/settings/contract").then(({ data }) => setTplText(data.template)).catch(() => {}); }} data-testid="contract-template-toggle" className={`flex items-center gap-2 border px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tplOpen ? "border-primary text-primary" : "border-white/20 text-white/60 hover:border-primary hover:text-primary"}`}>
            <ScrollText size={15} aria-hidden="true" /> Template
          </button>
          <button onClick={startNew} data-testid="new-proposal-button" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:-translate-y-0.5">
            <Plus size={15} aria-hidden="true" /> New Proposal
          </button>
        </div>
      </div>
      {tplOpen && (
        <div className="border border-primary/40 bg-primary/5 rounded-xl p-5 space-y-3" data-testid="contract-template-panel">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Standard Contract Template</p>
          <p className="text-[11px] text-white/45">Every new proposal starts with this text — you can tweak it per proposal before sending. Placeholders fill automatically: {"{client_name} {project_title} {address} {total} {date}"}</p>
          <textarea value={tplText} onChange={(e) => setTplText(e.target.value)} rows={12} data-testid="contract-template-text" className={`${inputCls} resize-y text-xs leading-relaxed`} />
          <button onClick={async () => { setTplSaving(true); try { await api.put("/admin/settings/contract", { template: tplText }); toast.success("Contract template saved"); } catch { toast.error("Could not save template"); } finally { setTplSaving(false); } }} disabled={tplSaving} data-testid="contract-template-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 rounded-lg disabled:opacity-50">
            {tplSaving ? "Saving..." : "Save Template"}
          </button>
        </div>
      )}
      {proposals.length === 0 && <p className="text-white/40 text-sm py-10 text-center" data-testid="proposals-empty">No proposals yet. Build one and text the link to your client.</p>}
      {proposals.length > 0 && (() => {
        const pendingList = proposals.filter((p) => p.status !== "accepted");
        const approvedList = proposals.filter((p) => p.status === "accepted");
        const shown = listTab === "approved" ? approvedList : pendingList;
        return (
      <>
      <div className="flex gap-2" data-testid="proposals-list-tabs">
        <button onClick={() => setListTab("pending")} data-testid="proposals-tab-pending" className={`flex-1 py-3 rounded-lg text-[11px] font-bold uppercase tracking-widest border transition-colors duration-200 ${listTab === "pending" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50 hover:border-primary hover:text-primary"}`}>
          Pending ({pendingList.length})
        </button>
        <button onClick={() => setListTab("approved")} data-testid="proposals-tab-approved" className={`flex-1 py-3 rounded-lg text-[11px] font-bold uppercase tracking-widest border transition-colors duration-200 ${listTab === "approved" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50 hover:border-primary hover:text-primary"}`}>
          Approved ({approvedList.length})
        </button>
      </div>
      {shown.length === 0 && (
        <p className="text-white/40 text-sm py-8 text-center" data-testid="proposals-tab-empty">
          {listTab === "approved" ? "No approved proposals yet — accepted ones land here." : "Nothing pending — every proposal is approved."}
        </p>
      )}
      <div className="space-y-3">
        {shown.map((p) => (
          <div key={p.id} className="border border-white/10 rounded-xl bg-card shadow-[0_4px_20px_rgba(0,0,0,0.3)] p-4" data-testid={`proposal-card-${p.id}`}>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${STATUS_BADGE[p.status] || STATUS_BADGE.draft}`} data-testid={`proposal-status-${p.id}`}>{STATUS_LABEL[p.status] || p.status}</span>
                {p.measurement_id && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-sky-400/20 text-sky-300" data-testid={`proposal-measured-${p.id}`}>Field measured</span>
                )}
              </div>
              <span className="text-[11px] text-white/40">{new Date(p.created_at).toLocaleDateString()}</span>
            </div>
            <p className="font-bold">{p.client_name}</p>
            <p className="text-sm text-white/50">{p.title} · {p.item_count} item{p.item_count === 1 ? "" : "s"}</p>
            <p className="font-display text-2xl text-primary mt-1">{p.status === "accepted" && p.accepted_total != null ? fmt(p.accepted_total) : `${fmt(p.base_total)} base`}</p>
            <div className="flex gap-2 mt-3">
              {p.published !== false && (
                <button onClick={() => copyLink(p)} data-testid={`proposal-copy-${p.id}`} className="flex-1 flex items-center justify-center gap-2 border border-primary/60 text-primary text-xs font-bold uppercase tracking-widest py-2.5 hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
                  <Copy size={13} aria-hidden="true" /> Copy Link
                </button>
              )}
              <button onClick={() => openProposal(p.id)} data-testid={`proposal-open-${p.id}`} className="flex-1 border border-white/20 text-white/70 text-xs font-bold uppercase tracking-widest py-2.5 hover:border-primary hover:text-primary transition-colors duration-300">
                {p.status === "accepted" ? "View" : p.status === "edit_requested" ? "Review" : "Edit"}
              </button>
              <button onClick={() => remove(p)} data-testid={`proposal-delete-${p.id}`} aria-label="Delete proposal" className="px-3.5 border border-red-400/30 text-red-400 hover:bg-red-400/10 transition-colors duration-300">
                <Trash2 size={14} />
              </button>
            </div>
            {p.published === false ? (
              <div className="mt-2 space-y-1.5" data-testid={`proposal-unsent-${p.id}`}>
                <button onClick={() => publish(p)} data-testid={`proposal-publish-${p.id}`} className="w-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest py-3 transition-transform duration-300 hover:-translate-y-0.5">
                  Send to client — make it visible
                </button>
                <p className="text-[10px] text-white/35 text-center">Hidden from the client's portal and link until you send it</p>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => downloadPdf(p)} data-testid={`proposal-pdf-${p.id}`} className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-white/60 text-[11px] font-bold uppercase tracking-widest py-2.5 hover:border-primary hover:text-primary transition-colors duration-300">
                    <FileDown size={13} aria-hidden="true" /> PDF
                  </button>
                  {p.client_email && (
                    <button onClick={() => emailLink(p)} data-testid={`proposal-email-${p.id}`} className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-white/60 text-[11px] font-bold uppercase tracking-widest py-2.5 hover:border-primary hover:text-primary transition-colors duration-300">
                      <Mail size={13} aria-hidden="true" /> Email Link
                    </button>
                  )}
                  {p.client_phone && (
                    <button onClick={() => textLink(p)} data-testid={`proposal-text-${p.id}`} className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-white/60 text-[11px] font-bold uppercase tracking-widest py-2.5 hover:border-primary hover:text-primary transition-colors duration-300">
                      <MessageSquare size={13} aria-hidden="true" /> Text Link
                    </button>
                  )}
                </div>
                {(p.status === "draft" || p.status === "viewed") && (
                  <button onClick={() => publish(p, false)} data-testid={`proposal-unpublish-${p.id}`} className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-white/30 hover:text-red-300 transition-colors duration-200">
                    Pull back — hide from client
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
      </>
        );
      })()}
    </div>
  );
};
